import { DateOnly } from "../lib/DateOnly";
import { skipToken } from "@reduxjs/toolkit/query";
import { Link } from "wouter-preact";
import { CalendarIcon, CheckIcon, ChevronRightIcon, FileTextIcon } from "../components/icons";
import { Money } from "../../../shared/money/Money";
import { MoneyDisplay } from "../components/MoneyDisplay";
import { StatusPill } from "../components/StatusPill";
import { ErrorPanel } from "../components/StatePanels";
import { selectPortalAccountScope, selectPortalIdentity } from "../features/auth/authSelectors";
import { useSiteConfig } from "../features/tenant/SiteConfigContext";
import { portalOverviewView } from "../features/overview/portalOverviewView";
import { useAppSelector } from "../store/hooks";
import { useGetPortalOverviewQuery, type PortalOverview } from "../store/portalOverviewApi";
import { getApiFailureMessage } from "../lib/gboApiClient";
import { routes } from "../lib/route";
import "../features/overview/portalOverview.css";

export function PortalOverviewPage() {
  const scope = useAppSelector(selectPortalAccountScope);
  const identity = useAppSelector(selectPortalIdentity);
  const query = useGetPortalOverviewQuery(identity?.customerId && scope ? scope : skipToken, {
    refetchOnMountOrArgChange: true,
  });
  if (query.isError)
    return (
      <ErrorPanel
        title="Unable to load your overview"
        message={getApiFailureMessage(query.error, "Please try again.")}
        onRetry={() => void query.refetch()}
      />
    );
  if (!query.currentData || query.currentData.customer.id !== identity?.customerId)
    return <OverviewSkeleton />;
  return <PortalOverviewContent overview={query.currentData} />;
}

export function PortalOverviewContent({ overview }: { overview: PortalOverview }) {
  const site = useSiteConfig();
  const dates = DateOnly.inTimezone(site.timezone);
  const today = DateOnly.fromInstant(new Date(), site.timezone);
  const view = portalOverviewView.create(overview, today);
  return (
    <div class="portal-overview">
      <header class="overview-heading">
        <h1>{overview.customer.name}</h1>
        <p class="muted">{overview.customer.billingAddress.formattedAddress}</p>
      </header>
      <section class="overview-balance" aria-label="Account balance">
        <div class="overview-balance-facts">
          <span class="muted">{view.balanceLabel}</span>
          <MoneyDisplay value={view.balance} size="large" />
          <p class={`overview-due${view.paidMessage ? " overview-paid" : ""}`}>
            {view.paidMessage ? (
              <>
                <CheckIcon size={18} />
                {view.paidMessage}
              </>
            ) : (
              <>
                {view.dueLabel && (
                  <>
                    <CalendarIcon size={16} />
                    {view.dueLabel}
                    <span aria-hidden="true">·</span>
                  </>
                )}
                {overview.openInvoices.count} open{" "}
                {overview.openInvoices.count === 1 ? "invoice" : "invoices"}
              </>
            )}
          </p>
        </div>
        {view.canPay && (
          <div class="overview-pay-actions">
            <Link href={`~${routes.portal.pay(true)}`} className="overview-alternate-pay">
              Pay a different amount
            </Link>
            <Link href={`~${routes.portal.pay()}`} className="primary-action overview-pay">
              Pay {Money.format(overview.accountBalance)}
            </Link>
          </div>
        )}
      </section>
      <div class="overview-lists">
        <section class="overview-list" aria-labelledby="overview-invoices-title">
          <div class="overview-list-card">
            <h2 id="overview-invoices-title">Open invoices</h2>
            {overview.openInvoices.items.length === 0 ? (
              <p class="overview-empty">You have no open invoices.</p>
            ) : (
              <ul>
                {overview.openInvoices.items.map((invoice) => {
                  const status = portalOverviewView.invoiceStatus(invoice);
                  return (
                    <li key={invoice.reference}>
                      <Link
                        className="overview-row"
                        href={`~${routes.portal.invoice(invoice.reference)}`}
                      >
                        <span class="overview-icon">
                          <FileTextIcon size={18} />
                        </span>
                        <span class="overview-row-details">
                          <strong>{invoice.number}</strong>
                          <span>
                            Due {dates.format(invoice.dueOn, { year: "when-different" })}
                            <span class="overview-mobile-status"> · {status}</span>
                          </span>
                        </span>
                        <span class="overview-desktop-status">
                          <StatusPill tone={status === "Open" ? "info" : "warning"}>
                            {status}
                          </StatusPill>
                        </span>
                        <MoneyDisplay value={invoice.balance} />
                        <ChevronRightIcon class="overview-chevron" size={16} />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <Link className="overview-view-all" href={`~${routes.portal.history("invoices")}`}>
            View all invoices
          </Link>
        </section>
        <section class="overview-list" aria-labelledby="overview-payments-title">
          <div class="overview-list-card">
            <h2 id="overview-payments-title">Recent payments</h2>
            {overview.recentPayments.length === 0 ? (
              <p class="overview-empty">No payments to show yet.</p>
            ) : (
              <ul>
                {overview.recentPayments.map((payment) => (
                  <li key={payment.id}>
                    <Link className="overview-row" href={`~${routes.portal.payment(payment.id)}`}>
                      <span class="overview-icon overview-icon--success">
                        <CheckIcon size={18} />
                      </span>
                      <span class="overview-row-details">
                        <strong>
                          {DateOnly.format(
                            DateOnly.fromInstant(payment.effectiveAt, site.timezone),
                          )}
                        </strong>
                        <span>{portalOverviewView.paymentDetail(payment)}</span>
                      </span>
                      <MoneyDisplay value={payment.totalReceived} />
                      <ChevronRightIcon class="overview-chevron" size={16} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Link className="overview-view-all" href={`~${routes.portal.history("payments")}`}>
            View all payments
          </Link>
        </section>
      </div>
      <p class="overview-help">Questions about your bill? Contact {site.displayName}.</p>
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div
      class="portal-overview overview-skeleton"
      role="status"
      aria-label="Loading your overview"
      aria-busy="true"
    >
      <div class="overview-heading">
        <div class="overview-skeleton-line" />
        <div class="overview-skeleton-line" />
      </div>
      <div class="overview-balance">
        <div class="overview-skeleton-line" />
        <div class="overview-skeleton-line" />
      </div>
      <div class="overview-lists">
        {[0, 1].map((i) => (
          <div key={i} class="overview-list-card">
            {[0, 1, 2].map((j) => (
              <div key={j} class="overview-skeleton-line" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
